import React from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody, ModalHeader, Label } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { actionResetIntranetAdministratorPassword } from 'store/administrator/actions';

import { ReactComponent as CloseButtonSvg } from 'assets/images/location/close-button-svg.svg';
import {
  DEPARTMENTS,
  DEPARTMENT_CS,
  DEPARTMENT_FN,
  DEPARTMENT_MKT,
  DEPARTMENT_PS,
  ROLE_AE,
  ROLE_AM,
  ROLE_CSE,
  ROLE_CSL,
  ROLE_CSM,
  ROLE_FL,
  ROLE_ML,
  ROLE_MM,
  ROLE_PSE,
  ROLE_PSL,
  ROLE_PSM,
  getExtranetFeatures,
  getIntranetFeatures,
} from 'constants/department';
import Button from 'components/App/Button';
import AppAvatar from 'components/App/Avatar';
import AppBadge from 'components/App/AppBadge';

import styles from './Confirm.module.scss';

function Confirm(props) {
  const {
    isOpen,
    onToggleModal,
    accountInfo,
    actionCreateInternalAccount,
    isLoadingCreateInternalAccount,
    editAccountId,
    actionUpdateInternalAccount,
    isLoadingUpdateInternalAccount,
    onRefreshList,
  } = props;

  const buildAccountPayload = React.useCallback(() => {
    const {
      email,
      username,
      password,
      avatar,
      createdAt,
      _id,
      market,
      markets,
      ...restAccountInfo
    } = accountInfo;

    const basePayload = {
      ...restAccountInfo,
      ...(editAccountId ? {} : { email, password, username }),
    };

    if (accountInfo.department === ROLE_PSM) {
      return {
        ...basePayload,
        markets: markets || [],
      };
    }

    return basePayload;
  }, [accountInfo, editAccountId]);

  const dispatch = useDispatch();

  const handleCreate = React.useCallback(() => {
    if (editAccountId) {
      const handlePasswordReset = () => {
        if (accountInfo.password && accountInfo.password.trim() !== '') {
          dispatch(
            actionResetIntranetAdministratorPassword({
              id: editAccountId,
              result: { password: accountInfo.password },
              callBack: () => {
                onToggleModal();
                onRefreshList();
              },
            }),
          );
        } else {
          onToggleModal();
          onRefreshList();
        }
      };

      actionUpdateInternalAccount({
        accountInfo: { ...buildAccountPayload(), id: editAccountId },
        callback: handlePasswordReset,
      });
    } else {
      actionCreateInternalAccount({
        accountInfo: buildAccountPayload(),
        callback: () => {
          onToggleModal();
          onRefreshList();
        },
      });
    }
  }, [
    actionCreateInternalAccount,
    actionUpdateInternalAccount,
    buildAccountPayload,
    editAccountId,
    onToggleModal,
    onRefreshList,
    accountInfo.password,
    dispatch,
  ]);

  const departmentCurrent = React.useMemo(() => DEPARTMENTS.reduce((found, dept) => {
    if (found) return found;
    if (dept.value === accountInfo.department) return dept;

    const roleFound = dept.roles?.find((role) => role.value === accountInfo.department);
    return roleFound || found;
  }, undefined), [accountInfo.department]);

  const departmentBadgeColor = React.useMemo(() => {
    switch (departmentCurrent?.value) {
      case DEPARTMENT_PS:
      case ROLE_PSL:
      case ROLE_PSM:
      case ROLE_PSE:
        return 'info';
      case DEPARTMENT_CS:
      case ROLE_CSL:
      case ROLE_CSM:
      case ROLE_CSE:
        return 'warning';
      case DEPARTMENT_FN:
      case ROLE_FL:
      case ROLE_AM:
      case ROLE_AE:
        return 'success';
      case DEPARTMENT_MKT:
      case ROLE_ML:
      case ROLE_MM:
        return 'caution';
      default:
        return 'neutral';
    }
  }, [departmentCurrent]);

  const extranetFeatures = React.useMemo(() =>
    getExtranetFeatures(accountInfo?.department), [accountInfo?.department]);

  const intranetFeatures = React.useMemo(() =>
    getIntranetFeatures(accountInfo?.department), [accountInfo?.department]);

  const CloseButton = React.useMemo(
    () => (
      <button
        className={styles.ButtonClose}
        onClick={onToggleModal}
        type="button"
      >
        <CloseButtonSvg />
      </button>
    ),
    [onToggleModal],
  );

  return (
    <Modal
      fade
      size="lg"
      isOpen={isOpen}
      toggle={onToggleModal}
      className={styles.Modal}
    >
      <ModalHeader
        toggle={onToggleModal}
        className={styles.ModalHeader}
        close={CloseButton}
      />

      <ModalBody className={styles.ModalBody}>
        <Label className="Title">
          Người dùng này có thể truy cập được những chức năng sau:
        </Label>
        <hr className="Divider" />

        <div className="UserCard">
          <div className="UserCard__Profile">
            <AppAvatar name={accountInfo.fullName} />
            <div className="UserCard__Info">
              <p className="UserCard__Name">{accountInfo.fullName}</p>
              <p className="UserCard__Email">{accountInfo.email}</p>
            </div>
          </div>

          <AppBadge
            color={departmentBadgeColor}
            className="UserCard__Department"
          >
            {departmentCurrent?.label}
          </AppBadge>
        </div>

        <div className="IntranetTable">
          <div className="IntranetTable__Row IntranetTable__Row__Extranet">
            <div className="IntranetTable__Cell">Extranet</div>
            <div className="IntranetTable__Cell">
              {extranetFeatures}
            </div>
          </div>
          <div className="IntranetTable__Row">
            <div className="IntranetTable__Cell">Intranet</div>
            <div className="IntranetTable__Cell">
              {intranetFeatures}
            </div>
          </div>
        </div>
        <p className="IntranetTable__Description">Người dùng có thể truy cập extranet của toàn bộ chỗ nghỉ.</p>

        <hr className="Divider" />

        <div className="Button__Wrapper">
          <Button
            classes="Button Button__Cancel"
            type="button"
            onClick={onToggleModal}
          >
            Hủy
          </Button>

          <Button
            classes="Button Button__Submit"
            type="button"
            onClick={handleCreate}
            loading={editAccountId ?
              isLoadingUpdateInternalAccount
              : isLoadingCreateInternalAccount}
          >
            {editAccountId ? 'Lưu' : 'Tạo'}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
}

Confirm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onToggleModal: PropTypes.func.isRequired,
  accountInfo: PropTypes.instanceOf(Object),
  actionCreateInternalAccount: PropTypes.func,
  isLoadingCreateInternalAccount: PropTypes.bool,
  editAccountId: PropTypes.string,
  actionUpdateInternalAccount: PropTypes.func,
  isLoadingUpdateInternalAccount: PropTypes.bool,
  onRefreshList: PropTypes.func,
};

Confirm.defaultProps = {
  accountInfo: {},
  actionCreateInternalAccount: () => {},
  isLoadingCreateInternalAccount: false,
  editAccountId: null,
  actionUpdateInternalAccount: () => {},
  isLoadingUpdateInternalAccount: false,
  onRefreshList: () => {},
};

export default React.memo(Confirm);
